import { useAuth, useSignIn, useSignUp, useUser } from '@clerk/clerk-react';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API_BASE_URL } from '@/config';
import { signOutExplicitly } from '@/lib/authSession';

type InviteState =
  | 'loading'
  | 'processing'
  | 'linking'
  | 'success'
  | 'error'
  | 'no_ticket'
  | 'needs_info';

interface SignUpFields {
  firstName: string;
  lastName: string;
  password: string;
}

/**
 * AcceptInvite handles Clerk invitation redirects with __clerk_ticket parameter.
 *
 * Flow:
 * 1. Admin invites user via Users & Roles
 * 2. Clerk sends email with invitation link
 * 3. User clicks link -> redirected to this page with __clerk_ticket and __clerk_status params
 * 4. Based on __clerk_status:
 *    - 'sign_up': Create new account with ticket (may need additional info)
 *    - 'sign_in': Sign in existing account with ticket
 * 5. After Clerk auth completes, link to D1 user via /auth/me
 * 6. Redirect to appropriate dashboard
 */
export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoaded: userLoaded, isSignedIn, user } = useUser();
  const { getToken, signOut } = useAuth();
  const { signUp, setActive: setSignUpActive, isLoaded: signUpLoaded } = useSignUp();
  const { signIn, setActive: setSignInActive, isLoaded: signInLoaded } = useSignIn();

  const [state, setState] = useState<InviteState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('Initializing...');
  const [fields, setFields] = useState<SignUpFields>({ firstName: '', lastName: '', password: '' });

  // Prevent multiple processing attempts
  const processingRef = useRef(false);
  const linkedRef = useRef(false);

  // Extract params from URL
  const ticket = searchParams.get('__clerk_ticket');
  const clerkStatus = searchParams.get('__clerk_status'); // 'sign_up', 'sign_in', or null

  // Link to D1 user after Clerk auth completes
  const linkToD1User = useCallback(async () => {
    if (linkedRef.current) return;
    linkedRef.current = true;

    console.log('[AcceptInvite] Linking to D1 user...');
    setStatusMessage('Finalizing your account...');

    try {
      // Try to get token with retries (mobile browsers may need extra time)
      let token = await getToken();
      if (!token) {
        console.warn('[AcceptInvite] Token not ready, retrying...');
        for (let i = 0; i < 5; i++) {
          await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
          token = await getToken();
          if (token) break;
        }
      }
      if (!token) {
        console.error('[AcceptInvite] No token available after retries');
        setState('error');
        setErrorMessage('Unable to authenticate. Please try signing in manually.');
        return;
      }

      // Call /auth/me which will auto-link the user based on email
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('[AcceptInvite] /auth/me response:', data);

      if (data.success && data.data?.role) {
        const role = data.data.role;
        console.log('[AcceptInvite] Successfully linked, role:', role);
        setState('success');

        // Route directly to role-specific dashboard using navigate (no page reload)
        const dashboardPath =
          role === 'hq_admin'
            ? '/admin'
            : role === 'agent'
              ? '/agent'
              : role === 'affiliate_owner' || role === 'affiliate_coach'
                ? '/affiliate'
                : role === 'member'
                  ? '/member'
                  : role === 'individual'
                    ? '/individual'
                    : '/';

        setTimeout(() => {
          navigate(dashboardPath, { replace: true });
        }, 1500);
      } else if (data.error === 'PROFILE_NOT_FOUND') {
        // User exists in Clerk but not in D1 - try link-invited-user endpoint
        const invitedUserId = user?.publicMetadata?.invited_user_id as string | undefined;
        if (invitedUserId) {
          console.log('[AcceptInvite] Trying link-invited-user with ID:', invitedUserId);
          const linkResponse = await fetch(`${API_BASE_URL}/auth/link-invited-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ invited_user_id: invitedUserId }),
          });

          const linkData = await linkResponse.json();
          if (linkData.success) {
            console.log('[AcceptInvite] Successfully linked via invited_user_id');
            setState('success');
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 1500);
            return;
          }
        }

        // No D1 user found - redirect to role selection
        console.log('[AcceptInvite] No D1 user found, redirecting to role selection');
        window.location.href = '/select-role';
      } else {
        console.error('[AcceptInvite] Unexpected /auth/me response:', data);
        setState('error');
        setErrorMessage(data.error || 'Failed to set up your account');
      }
    } catch (error) {
      console.error('[AcceptInvite] Error linking to D1:', error);
      setState('error');
      setErrorMessage('An error occurred while setting up your account');
    }
  }, [getToken, navigate, user?.publicMetadata?.invited_user_id]);

  // Process the ticket for sign-up flow
  const processSignUpTicket = useCallback(async () => {
    if (!signUp || !ticket) return;

    console.log('[AcceptInvite] Processing sign-up ticket...');
    setStatusMessage('Creating your account...');

    try {
      const result = await signUp.create({
        strategy: 'ticket',
        ticket,
      });

      console.log('[AcceptInvite] Sign-up result:', {
        status: result.status,
        createdSessionId: result.createdSessionId,
        createdUserId: result.createdUserId,
        missingFields: result.missingFields,
        unverifiedFields: result.unverifiedFields,
        verifications: result.verifications,
      });

      if (result.status === 'complete') {
        // Sign-up complete - activate session
        if (result.createdSessionId) {
          console.log('[AcceptInvite] Activating session with ID:', result.createdSessionId);
          try {
            await setSignUpActive({ session: result.createdSessionId });
            console.log('[AcceptInvite] setActive completed successfully');
          } catch (activeError) {
            console.error('[AcceptInvite] setActive failed:', activeError);
            setState('error');
            setErrorMessage('Failed to activate session. Please sign in manually.');
            return;
          }
          // Wait for session to fully establish before proceeding
          console.log('[AcceptInvite] Session activated, waiting 2s for it to settle...');
          await new Promise((resolve) => setTimeout(resolve, 2000));
          setState('linking');
        } else {
          console.error('[AcceptInvite] No createdSessionId in complete result');
          setState('error');
          setErrorMessage('Account created but session failed. Please sign in manually.');
        }
      } else if (result.status === 'missing_requirements') {
        // Need additional info (firstName, lastName, password)
        console.log('[AcceptInvite] Missing requirements:', result.missingFields);
        setState('needs_info');
      } else {
        console.log('[AcceptInvite] Unexpected status:', result.status);
        setState('error');
        setErrorMessage(`Unable to complete sign-up (status: ${result.status}). Please try again.`);
      }
    } catch (error: unknown) {
      console.error('[AcceptInvite] Sign-up error:', error);
      const clerkError = error as { errors?: Array<{ message: string; code: string }> };

      // Check if the error is because user already exists
      if (clerkError.errors?.some((e) => e.code === 'form_identifier_exists')) {
        console.log('[AcceptInvite] User already exists, trying sign-in flow');
        processSignInTicket();
        return;
      }

      setState('error');
      setErrorMessage(clerkError.errors?.[0]?.message || 'Failed to create account');
    }
  }, [signUp, ticket, setSignUpActive]);

  // Process the ticket for sign-in flow
  const processSignInTicket = useCallback(async () => {
    if (!signIn || !ticket) return;

    console.log('[AcceptInvite] Processing sign-in ticket...');
    setStatusMessage('Signing you in...');

    try {
      const result = await signIn.create({
        strategy: 'ticket',
        ticket,
      });

      console.log('[AcceptInvite] Sign-in result status:', result.status);

      if (result.status === 'complete') {
        // Sign-in complete - activate session
        if (result.createdSessionId) {
          console.log('[AcceptInvite] Activating session with ID:', result.createdSessionId);
          try {
            await setSignInActive({ session: result.createdSessionId });
            console.log('[AcceptInvite] setActive completed successfully');
          } catch (activeError) {
            console.error('[AcceptInvite] setActive failed:', activeError);
            setState('error');
            setErrorMessage('Failed to activate session. Please sign in manually.');
            return;
          }
          // Wait for session to fully establish before proceeding
          console.log('[AcceptInvite] Session activated, waiting 2s for it to settle...');
          await new Promise((resolve) => setTimeout(resolve, 2000));
          setState('linking');
        } else {
          console.error('[AcceptInvite] No createdSessionId in sign-in result');
          setState('error');
          setErrorMessage('Sign in succeeded but session failed. Please try again.');
        }
      } else {
        console.log('[AcceptInvite] Unexpected sign-in status:', result.status);
        setState('error');
        setErrorMessage('Unable to sign in. Please try again.');
      }
    } catch (error: unknown) {
      console.error('[AcceptInvite] Sign-in error:', error);
      const clerkError = error as { errors?: Array<{ message: string }> };
      setState('error');
      setErrorMessage(clerkError.errors?.[0]?.message || 'Failed to sign in');
    }
  }, [signIn, ticket, setSignInActive]);

  // Handle additional info submission (when sign-up needs more fields)
  const handleSubmitInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;

    setState('processing');
    setStatusMessage('Completing your account...');

    try {
      const result = await signUp.update({
        firstName: fields.firstName,
        lastName: fields.lastName,
        password: fields.password,
      });

      console.log('[AcceptInvite] Update result status:', result.status);

      if (result.status === 'complete') {
        if (result.createdSessionId) {
          console.log('[AcceptInvite] Update complete, activating session...');
          try {
            await setSignUpActive({ session: result.createdSessionId });
            console.log('[AcceptInvite] Session activated, waiting 2s...');
            await new Promise((resolve) => setTimeout(resolve, 2000));
            setState('linking');
          } catch (activeError) {
            console.error('[AcceptInvite] setActive failed:', activeError);
            setState('error');
            setErrorMessage('Failed to activate session. Please sign in manually.');
          }
        } else {
          setState('error');
          setErrorMessage('Account created but no session. Please sign in manually.');
        }
      } else {
        console.log('[AcceptInvite] Update status:', result.status);
        setState('error');
        setErrorMessage('Unable to complete sign-up. Please try again.');
      }
    } catch (error: unknown) {
      console.error('[AcceptInvite] Update error:', error);
      const clerkError = error as { errors?: Array<{ message: string }> };
      setState('error');
      setErrorMessage(clerkError.errors?.[0]?.message || 'Failed to complete sign-up');
    }
  };

  // Main effect: process ticket when Clerk is ready
  useEffect(() => {
    // Wait for all Clerk components to load
    if (!userLoaded || !signUpLoaded || !signInLoaded) {
      return;
    }

    // A ticket ALWAYS takes priority over any existing session. Previously we
    // short-circuited to "linking" whenever isSignedIn was true — but if the
    // browser already held a *different* user's session, the invite linked to
    // the wrong account. If signed in with a ticket present, sign out first so
    // the ticket establishes the correct (invited) session.
    if (ticket) {
      if (processingRef.current) return;
      processingRef.current = true;

      const run = async () => {
        if (isSignedIn) {
          console.log('[AcceptInvite] Existing session + ticket — signing out first');
          try {
            await signOutExplicitly(signOut);
          } catch (e) {
            console.warn('[AcceptInvite] signOut before ticket failed (continuing):', e);
          }
        }
        console.log('[AcceptInvite] Starting ticket processing, status:', clerkStatus);
        setState('processing');
        if (clerkStatus === 'sign_in') {
          await processSignInTicket();
        } else {
          await processSignUpTicket();
        }
      };
      run();
      return;
    }

    // No ticket: if already signed in, link the current session; else error.
    if (isSignedIn) {
      console.log('[AcceptInvite] No ticket but signed in, linking to D1...');
      setState('linking');
      return;
    }

    console.log('[AcceptInvite] No ticket in URL');
    setState('no_ticket');
  }, [
    userLoaded,
    signUpLoaded,
    signInLoaded,
    isSignedIn,
    ticket,
    clerkStatus,
    signOut,
    processSignUpTicket,
    processSignInTicket,
  ]);

  // Effect: link to D1 when in linking state - with retry loop
  useEffect(() => {
    if (state !== 'linking') return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const checkInterval = 1000; // Check every second

    const checkAndLink = async () => {
      while (!cancelled && attempts < maxAttempts) {
        attempts++;
        console.log(`[AcceptInvite] Check ${attempts}/${maxAttempts}: isSignedIn=${isSignedIn}`);

        if (isSignedIn) {
          console.log('[AcceptInvite] User is signed in, proceeding to link');
          await linkToD1User();
          return;
        }

        // Wait before next check
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }

      if (!cancelled && !isSignedIn) {
        console.error('[AcceptInvite] User still not signed in after all attempts');
        setState('error');
        setErrorMessage(
          'Session failed to establish. Your account was created - please try signing in manually.'
        );
      }
    };

    checkAndLink();

    return () => {
      cancelled = true;
    };
  }, [state, isSignedIn, linkToD1User]);

  // Render loading state
  if (state === 'loading' || state === 'processing' || state === 'linking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="text-center animate-fade-in">
          <div className="flex justify-center mb-6">
            <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gradient-gold mb-4">ErgShoot</h1>
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{statusMessage}</p>
        </div>
      </div>
    );
  }

  // Render needs_info state (collecting additional user info)
  if (state === 'needs_info') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-16 w-auto" />
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold">Almost there!</h1>
            <p className="mt-2 text-muted-foreground">Complete your account setup</p>
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle>Your Information</CardTitle>
              <CardDescription>
                Please provide the following details to complete sign-up
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitInfo} className="space-y-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium mb-1">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    required
                    value={fields.firstName}
                    onChange={(e) => setFields({ ...fields, firstName: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium mb-1">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    required
                    value={fields.lastName}
                    onChange={(e) => setFields({ ...fields, lastName: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-1">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    value={fields.password}
                    onChange={(e) => setFields({ ...fields, password: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
                </div>
                <Button type="submit" className="w-full">
                  Complete Sign Up
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Render success state
  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
        <Card className="w-full max-w-md animate-fade-in">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <CardTitle className="text-xl text-gradient-gold">Welcome to ErgShoot!</CardTitle>
            <CardDescription>Your account has been set up successfully</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-4">Redirecting to your dashboard...</p>
            <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render no_ticket state
  if (state === 'no_ticket') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
        <Card className="w-full max-w-md animate-fade-in">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
                <XCircle className="h-8 w-8 text-amber-500" />
              </div>
            </div>
            <CardTitle className="text-xl">Invalid Invitation Link</CardTitle>
            <CardDescription>
              This link doesn't contain a valid invitation. Please use the link from your invitation
              email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              If you've already created an account, you can sign in directly.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
              <Button onClick={() => navigate('/register')}>Sign Up</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
          <CardDescription className="text-destructive">{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            The invitation may have expired or there was a problem processing it. Please try again
            or contact support.
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
